import {
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  EmailAuthProvider,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  linkWithCredential,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import type { User } from '../types';
import { getUserByEmail, saveUser } from './rtdb/users.service';
import { getApprovedRequestByEmail, updateAccessRequest } from './rtdb/accessRequests.service';

const googleProvider = new GoogleAuthProvider();

const MAGIC_LINK_EMAIL_KEY = 'magicLinkEmail';

/**
 * Sign in with Google
 * After successful Google sign-in, checks if user is whitelisted in Firestore
 */
export const signInWithGoogle = async (): Promise<User> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const firebaseUser = result.user;

    if (!firebaseUser.email) {
      await firebaseSignOut(auth);
      throw new Error('No email associated with this Google account.');
    }

    // Check if user exists and is whitelisted in Firestore (by email)
    const userData = await getUserByEmail(firebaseUser.email);

    if (!userData) {
      // No user record yet – check for an approved access request
      const approvedRequest = await getApprovedRequestByEmail(firebaseUser.email);
      if (approvedRequest && approvedRequest.approvedRole) {
        const role = approvedRequest.approvedRole;
        const newUser: User = {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          email: firebaseUser.email.toLowerCase(),
          isAdmin: false,
          isFamily: role === 'family',
          isFamilyMember: role === 'familyMember',
          status: 'approved',
        };
        await saveUser(newUser);
        await updateAccessRequest(approvedRequest.id, { status: 'rejected' }); // mark as used
        return newUser;
      }

      // Not in users table and no approved request – deny access
      await firebaseSignOut(auth);
      throw new Error('Zugang nicht freigeschaltet. Bitte wende dich an einen Admin.');
    }

    // Check if user is approved
    if (userData.status === 'blocked') {
      await firebaseSignOut(auth);
      throw new Error('Your account has been blocked. Please contact an administrator.');
    }

    if (userData.status === 'pending') {
      await firebaseSignOut(auth);
      throw new Error('Your account is pending approval. Please wait for an administrator to approve your access.');
    }

    // Migration is handled by AuthContext to ensure it completes
    // before Firestore listeners are initialized.
    // Just return the user data as-is here.
    return userData;
  } catch (error: any) {
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled');
    }
    throw error;
  }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

/**
 * Listen to authentication state changes
 */
export const onAuthChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current Firebase user
 */
export const getCurrentFirebaseUser = (): FirebaseUser | null => {
  return auth.currentUser;
};

/**
 * Fetch user data from Firestore by email
 */
export const getUserDataByEmail = async (email: string): Promise<User | null> => {
  try {
    return await getUserByEmail(email);
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Send a passwordless sign-in link to the given email.
 * The user will receive an email with a link that returns them to `continueUrl`
 * (defaults to the current page), where `completeMagicLinkSignIn` must be called.
 */
export const sendMagicLink = async (
  email: string,
  continueUrl?: string
): Promise<void> => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    throw new Error('Email is required');
  }

  const url = continueUrl || `${window.location.origin}/login`;

  await sendSignInLinkToEmail(auth, normalized, {
    url,
    handleCodeInApp: true,
  });

  window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, normalized);
};

/**
 * Send a magic link on behalf of someone else (e.g. admin approving an access
 * request). The email is encoded in the continue URL so the recipient's browser
 * can complete sign-in without localStorage.
 */
export const sendMagicLinkForUser = async (email: string): Promise<void> => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    throw new Error('Email is required');
  }

  const continueUrl = `${window.location.origin}/login?loginEmail=${encodeURIComponent(normalized)}`;

  await sendSignInLinkToEmail(auth, normalized, {
    url: continueUrl,
    handleCodeInApp: true,
  });
};

/**
 * True if the current URL is a Firebase email sign-in link.
 */
export const isMagicLinkUrl = (url: string = window.location.href): boolean => {
  return isSignInWithEmailLink(auth, url);
};

/**
 * Complete a magic link sign-in. Resolves the email from (in order):
 * 1. Explicit override (e.g. user re-entered it)
 * 2. localStorage (set when the user themselves requested the link)
 * 3. `loginEmail` URL param (set when admin sent the link on the user's behalf)
 *
 * On success, Firebase auth state changes and AuthContext takes over — the
 * same whitelist / invite checks apply as for Google sign-in.
 */
export const completeMagicLinkSignIn = async (
  emailOverride?: string
): Promise<void> => {
  const url = window.location.href;

  if (!isSignInWithEmailLink(auth, url)) {
    throw new Error('Not a magic link URL');
  }

  // Try URL param (from admin-sent link)
  const urlEmail = new URL(window.location.href).searchParams.get('loginEmail')?.trim().toLowerCase() || '';

  const email =
    emailOverride?.trim().toLowerCase() ||
    window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY) ||
    urlEmail ||
    '';

  if (!email) {
    throw new Error('MISSING_EMAIL');
  }

  await signInWithEmailLink(auth, email, url);
  window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
};

/**
 * Send a "set your password" link via our own mailer (Resend), rather than
 * Firebase's default noreply@<project>.firebaseapp.com sender which is
 * routinely spam-filed. Resolves even for unknown addresses — the backend
 * deliberately stays silent about which accounts exist.
 */
export const sendPasswordResetLink = async (email: string): Promise<void> => {
  const fn = httpsCallable(functions, 'sendPasswordResetLink');
  await fn({ email: email.trim().toLowerCase() });
};

/**
 * Sign in with email and password (for returning non-Google users).
 */
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  const result = await firebaseSignInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
  const firebaseUser = result.user;

  if (!firebaseUser.email) {
    await firebaseSignOut(auth);
    throw new Error('Kein E-Mail mit diesem Konto verknüpft.');
  }

  const userData = await getUserByEmail(firebaseUser.email);

  if (!userData) {
    await firebaseSignOut(auth);
    throw new Error('Zugang nicht freigeschaltet. Bitte wende dich an einen Admin.');
  }

  if (userData.status === 'blocked') {
    await firebaseSignOut(auth);
    throw new Error('Dein Konto wurde gesperrt.');
  }

  if (userData.status === 'pending') {
    await firebaseSignOut(auth);
    throw new Error('Dein Konto wartet noch auf Freigabe.');
  }

  return userData;
};

/**
 * Link a password to the currently signed-in user (after first magic-link sign-in).
 * Returns true on success.
 */
export const linkPasswordToAccount = async (password: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('Kein Benutzer angemeldet.');
  }

  const credential = EmailAuthProvider.credential(user.email, password);
  await linkWithCredential(user, credential);
};

/**
 * Check whether the current Firebase user needs to set a password.
 * True if they signed in via magic link and have no password or Google provider.
 */
export const needsPasswordSetup = (firebaseUser: FirebaseUser | null): boolean => {
  if (!firebaseUser) return false;
  const providers = firebaseUser.providerData.map(p => p.providerId);
  return !providers.includes('password') && !providers.includes('google.com');
};
